import { ArrowUpRight, Linkedin, Instagram, Facebook } from "lucide-react";
import { scrollToId, Reveal } from "@/components/Reveal";
import { waLink, WA_DEFAULT_MSG, WA_RESUME_MSG } from "@/utils/whatsapp";
import Logo from "@/components/Logo";
import WhatsAppIcon from "@/components/WhatsAppFloat";
import { SOCIALS } from "@/data/content";

const SOCIAL_ICONS = {
  whatsapp: WhatsAppIcon,
  instagram: Instagram,
  linkedin: Linkedin,
  facebook: Facebook,
};

const SOCIAL_COLORS = {
  whatsapp: "#25D366",
  instagram: "#E1306C",
  linkedin: "#0A66C2",
  facebook: "#1877F2",
};

const CHAPTERS = [
  { num: "01", label: "The Process", href: "#process" },
  { num: "02", label: "Who We Are", href: "#about" },
  { num: "03", label: "What We Do", href: "#services" },
  { num: "05", label: "Stories", href: "#stories" },
  { num: "07", label: "Get In Touch", href: "#contact" },
];

export default function Footer() {
  return (
    <footer className="border-t border-white/10 pt-20 pb-10 overflow-hidden" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16">
          <div className="md:col-span-5">
            <div>
              <Logo imgClass="h-16" />
            </div>
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-sm">
              Comprehensive HR and business support services — recruitment,
              employee development, compliance and growth strategy for
              employers and employees alike.
            </p>
            <a
              href={waLink(WA_DEFAULT_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#25D366] hover:brightness-110 transition"
              data-testid="footer-whatsapp-link"
            >
              WhatsApp us — +91 62679 98659 <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
          <div className="md:col-span-4">
            <div className="flex flex-col gap-3">
              {CHAPTERS.map((c) => (
                <button
                  key={c.num}
                  onClick={() => scrollToId(c.href)}
                  className="flex items-center gap-4 text-left group"
                  data-testid={`footer-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span className="font-heading text-xs text-primary">{c.num}</span>
                  <span className="text-zinc-300 group-hover:text-white transition-colors duration-300">
                    {c.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="text-sm text-zinc-400 leading-relaxed">
              Register for future job opportunities by sending your updated
              resume via email given below or on{" "}
              <a
                href={waLink(WA_RESUME_MSG)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#25D366] font-semibold hover:underline"
                data-testid="footer-resume-whatsapp-link"
              >
                WhatsApp
              </a>{" "}
              and stay connected with us.
            </p>
            <a
              href="mailto:info@hr99global.com"
              className="mt-3 block text-sm text-zinc-300 hover:text-primary transition-colors"
              data-testid="footer-email-link"
            >
              info@hr99global.com
            </a>
          </div>
        </div>

        <Reveal className="border border-white/10 bg-zinc-900/40 p-8 md:p-12 mb-4">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p
                className="text-xs uppercase tracking-[0.25em] text-primary font-semibold mb-3"
                data-testid="socials-overline"
              >
                Get Jobs Updates On
              </p>
              <h3 className="font-heading text-3xl md:text-4xl font-medium text-white max-w-xl">
                Follow us for new vacancies & regular interview status.
              </h3>
            </div>
            <p className="text-sm text-zinc-500 max-w-xs leading-relaxed">
              Join our WhatsApp Channel to get interview updates and fresh
              openings before anyone else.
            </p>
          </div>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="social-links-grid">
            {SOCIALS.map((s) => {
              const Icon = SOCIAL_ICONS[s.icon];
              return (
                <a
                  key={s.key}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between gap-3 border border-white/10 px-5 py-5 hover:border-primary/60 hover:bg-zinc-900 transition-colors duration-300"
                  data-testid={`social-link-${s.key}`}
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <span style={{ color: SOCIAL_COLORS[s.icon] }} className="shrink-0">
                      <Icon className="w-6 h-6" />
                    </span>
                    <span className="text-sm font-semibold text-white truncate">
                      {s.label}
                    </span>
                  </span>
                  <ArrowUpRight className="w-4 h-4 shrink-0 text-zinc-600 group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                </a>
              );
            })}
          </div>
        </Reveal>
      </div>

      <Reveal>
        <p
          className="font-heading text-outline text-center font-bold uppercase leading-none select-none text-[7.5vw] whitespace-nowrap tracking-tight"
          data-testid="footer-big-text"
        >
          HR99 Global Services
        </p>
      </Reveal>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-10 pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-zinc-600">
          © 2026 HR99 Global Services. All rights reserved.
        </p>
        <p className="text-xs text-zinc-600">
          Hire top talent in just 2 weeks.
        </p>
      </div>
    </footer>
  );
}
